import React, {CSSProperties} from 'react'
import {
    getTotalCo2KgEEmissionsAndCalculationStatus,
    getTotalCo2KgEEmissionsAndCalculationStatusIncludingReturnActivity,
    TotalCo2KgEEmissionsAndCalculationStatusProps
} from "../../../../utils/getter/emissions";
import {useTranslation} from "react-i18next";
import {AllActivityTypes, AllActivityTypesArray} from "../../../../types-enums/activity";
import {isActivityFlightState} from "../../../../utils/check/type-guards";
import {
    CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL,
    CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL
} from "../../../../api/model/common";
import {ColorKeys, Text} from "atmosfair-ui";
import clsx from "clsx";
import {Tooltip} from "@mui/material";
import {generateFailedOrSuccessfulFlightLegsString} from "../../../../utils/string/flight";
import {getActivityPersonCount} from "../../../../utils/getter/activities";
import {formatCo2EPerPersonString, formatCo2ETotalString} from "../../../../utils/string/common";

interface CalculatedCarBusEmissionsProps {
    activity: AllActivityTypes,
    allActivities: AllActivityTypesArray,
    totalCo2KgEIncludingReturnEmissionValues: boolean,
    shortEmissions?: boolean,
    textClasses?: string | string[],
    textStyles?: CSSProperties,
    textColor?: ColorKeys,
}

const ActivityEmissionDisplay = ({
                                     activity,
                                     allActivities,
                                     shortEmissions,
                                     textClasses,
                                     textStyles,
                                     textColor,
                                     totalCo2KgEIncludingReturnEmissionValues
                                 }: CalculatedCarBusEmissionsProps) => {
    const {status} = getTotalCo2KgEEmissionsAndCalculationStatus(activity)
    const {i18n, t} = useTranslation()
    const {resolvedLanguage} = i18n
    const peopleCount = getActivityPersonCount(activity)

    const _setCo2KgEString = () => {
        let co2ETotalWithAdditionalValues: TotalCo2KgEEmissionsAndCalculationStatusProps["emissionKgCO2eTotal"] = null
        if (totalCo2KgEIncludingReturnEmissionValues) {
            co2ETotalWithAdditionalValues = getTotalCo2KgEEmissionsAndCalculationStatusIncludingReturnActivity(activity, allActivities).emissionKgCO2eTotal
        } else {
            co2ETotalWithAdditionalValues = getTotalCo2KgEEmissionsAndCalculationStatus(activity).emissionKgCO2eTotal
        }
        let co2KgEPerPerson
        if (peopleCount === null) {
            co2KgEPerPerson = t("ERRORS.COMMON.GENERAL")
        } else {
            co2KgEPerPerson = `${formatCo2EPerPersonString(co2ETotalWithAdditionalValues!, peopleCount, resolvedLanguage)}`
        }
        const co2KgETotal = `${formatCo2ETotalString(co2ETotalWithAdditionalValues!, resolvedLanguage)} ${i18n.t("GENERAL.TOTAL")}`

        if (shortEmissions) return co2KgETotal
        return `${co2KgEPerPerson} p.P. (${co2KgETotal})`
    }

    const _renderCo2KgEString = () => {
        let result
        if (status !== CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success) {
            let failureClass = ''
            let failureComponent: React.ReactNode
            if (status === CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed || status === null) {
                failureClass = "travel-components-table-cell-complete-failure"
                failureComponent = <Text
                    style={textStyles}
                    variant={"subtitleLarge"}
                    color={"white"}
                    ellipsis={true}
                    classes={clsx("travel-components-table-cell-failure-text", textClasses)}>
                    ERRORS.CALCULATION_COMPLETE_FAILURE
                </Text>
            } else {
                if (isActivityFlightState(activity)) {
                    failureClass = 'travel-components-table-cell-partial-failure'
                    failureComponent = <Tooltip
                        placement={"top"}
                        title={generateFailedOrSuccessfulFlightLegsString(activity, CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.failed, i18n)}>
                        <div>
                            <Text
                                style={textStyles}
                                variant={"subtitleLarge"}
                                color={textColor ?? "white"}
                                ellipsis={true}
                                classes={clsx("travel-components-table-cell-failure-text", textClasses)}>
                                ERRORS.CALCULATION_PARTIAL_FAILURE
                            </Text>
                        </div>
                    </Tooltip>
                }
            }
            result = <div
                className={clsx(
                    "travel-components-table-cell-failure",
                    failureClass
                )}>
                {failureComponent}
            </div>
        } else {
            result =
                <Text
                    classes={textClasses}
                    style={textStyles}
                    color={textColor}>{_setCo2KgEString()}</Text>
        }

        return result
    }

    return _renderCo2KgEString()
}

export default ActivityEmissionDisplay